import React from "react";
import {
  Box,
  Drawer,
  Stack,
  IconButton,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material";
import { Menu } from "lucide-react";
import { useTheme } from "../theme/useTheme";
import PropTypes from "prop-types";

/**
 * Componentes Sidebar personalizados usando MUI Box/Stack con estilos del theme
 * Reemplaza los componentes de /ui/sidebar.jsx
 */

// Constantes del Sidebar
const SIDEBAR_WIDTH = "16rem"; // 256px
const SIDEBAR_WIDTH_MOBILE = "18rem"; // 288px
const SIDEBAR_WIDTH_ICON = "4.5rem"; // 72px, vista comprimida (solo iconos)

// Layout "docked": el header ocupa todo el ancho (con el logo) y el sidebar va debajo,
// así al comprimirlo el logo no se ve afectado
// 216 = mismo ancho visible que el sidebar "inset" original:
// 16rem (224px, html a 14px) menos los 8px de margen derecho
const DOCKED_SIDEBAR_WIDTH = 216;
const DOCKED_SIDEBAR_WIDTH_ICON = 64;
const DOCKED_HEADER_HEIGHT = 64;
const DOCKED_TRANSITION = "200ms ease-in-out";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

// Contexto del Sidebar
const SidebarContext = React.createContext(null);

// Hook para usar el contexto del sidebar
const useSidebar = () => {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar debe usarse dentro de un SidebarProvider");
  }
  return context;
};

// Provider del Sidebar
const SidebarProvider = ({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  children,
  ...props
}) => {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [openMobile, setOpenMobile] = React.useState(false);
  // Un recorrido guiado puede fijar el drawer móvil abierto mientras resalta sus items
  const [mobileLocked, setMobileLocked] = React.useState(false);

  // Estado interno del sidebar
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // Guardar estado en localStorage
      localStorage.setItem("sidebar_state", openState.toString());
    },
    [setOpenProp, open]
  );

  // Helper para toggle del sidebar
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen]);

  // Shortcut de teclado para toggle
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      mobileLocked,
      setMobileLocked,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, mobileLocked, toggleSidebar]
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          width: "100%",
          "--sidebar-width": SIDEBAR_WIDTH,
        }}
        {...props}
      >
        {children}
      </Box>
    </SidebarContext.Provider>
  );
};

// Componente principal del Sidebar
const Sidebar = ({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  children,
  ...props
}) => {
  const { theme, neutrals } = useTheme();
  const { isMobile, state, openMobile, setOpenMobile, mobileLocked } = useSidebar();
  const isCollapsed = state === "collapsed";
  const isOffcanvasCollapsed =
    state === "collapsed" && collapsible === "offcanvas";
  // En modo "icon" el sidebar se comprime a una tira de iconos en vez de ocultarse
  const desktopWidth =
    state === "collapsed" && collapsible === "icon"
      ? SIDEBAR_WIDTH_ICON
      : SIDEBAR_WIDTH;

  if (collapsible === "none") {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          display: "flex",
          height: "100%",
          width: SIDEBAR_WIDTH,
          flexDirection: "column",
          borderRight: `1px solid ${neutrals.gray[200]}`,
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Drawer
        anchor={side}
        open={openMobile}
        onClose={() => {
          if (!mobileLocked) setOpenMobile(false);
        }}
        variant="temporary"
        ModalProps={{ disableEnforceFocus: mobileLocked, disableAutoFocus: mobileLocked }}
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            width: SIDEBAR_WIDTH_MOBILE,
            border: "none",
          },
        }}
        {...props}
      >
        <Box
          sx={{
            display: "flex",
            height: "100%",
            width: "100%",
            flexDirection: "column",
          }}
        >
          {children}
        </Box>
      </Drawer>
    );
  }

  if (variant === "docked") {
    return (
      <Box
        sx={{
          position: "fixed",
          top: DOCKED_HEADER_HEIGHT,
          bottom: 0,
          left: 0,
          zIndex: 1001,
          width: isCollapsed ? DOCKED_SIDEBAR_WIDTH_ICON : DOCKED_SIDEBAR_WIDTH,
          // mismo margen izquierdo que el sidebar original
          paddingLeft: isCollapsed ? 0 : "8px",
          boxSizing: "border-box",
          transition: `width ${DOCKED_TRANSITION}, padding-left ${DOCKED_TRANSITION}`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRight: `1px solid ${neutrals.gray[200]}`,
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }

  return (
    <>
      {/* Espacio del sidebar en desktop */}
      <Box
        sx={{
          width: isOffcanvasCollapsed ? 0 : desktopWidth,
          transition: "width 200ms ease-in-out",
          flexShrink: 0,
        }}
      />

      {/* Sidebar fijo */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          bottom: 0,
          left: isOffcanvasCollapsed ? `-${SIDEBAR_WIDTH}` : 0,
          zIndex: 10,
          width: desktopWidth,
          overflow: "hidden",
          transition: "left 200ms ease-in-out, width 200ms ease-in-out",
          display: { xs: "none", md: "flex" },
          padding: variant === "floating" || variant === "inset" ? 1 : 0,
        }}
        {...props}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            display: "flex",
            height: "100%",
            width: "100%",
            flexDirection: "column",
            borderRight:
              variant === "floating" || variant === "inset"
                ? `1px solid ${neutrals.gray[200]}`
                : `1px solid ${neutrals.gray[200]}`,
            borderRadius: variant === "floating" ? 2 : 0,
            boxShadow: variant === "floating" ? theme.shadows[1] : "none",
          }}
        >
          {children}
        </Box>
      </Box>
    </>
  );
};

// Trigger del Sidebar
// Botón redondo con borde e icono hamburguesa (mismo estilo que AveronCRMERP)
const SidebarTrigger = ({ sx = {}, ...props }) => {
  const { toggleSidebar, state } = useSidebar();
  const { theme, neutrals } = useTheme();

  return (
    <IconButton
      onClick={toggleSidebar}
      size="small"
      aria-label={
        state === "collapsed" ? "Expandir barra lateral" : "Contraer barra lateral"
      }
      sx={{
        width: 32,
        height: 32,
        flexShrink: 0,
        border: `1px solid ${neutrals.gray[200]}`,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.secondary,
        "&:hover": {
          backgroundColor: neutrals.gray[50],
        },
        ...sx,
      }}
      {...props}
    >
      <Menu size={16} />
    </IconButton>
  );
};

// Header del Sidebar
const SidebarHeader = ({ children, ...props }) => {
  const { neutrals } = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        padding: 0,
        borderBottom: `1px solid ${neutrals.gray[200]}`,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

// Content del Sidebar
const SidebarContent = ({ children, ...props }) => {
  return (
    <Box
      sx={{
        display: "flex",
        minHeight: 0,
        flex: 1,
        flexDirection: "column",
        gap: 0,
        overflowY: "auto",
        overflowX: "hidden",
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

// Footer del Sidebar
const SidebarFooter = ({ children, ...props }) => {
  const { neutrals } = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        padding: 0,
        borderTop: `1px solid ${neutrals.gray[200]}`,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

// Menu del Sidebar
const SidebarMenu = ({ children, ...props }) => {
  return (
    <Stack
      component="ul"
      sx={{
        display: "flex",
        width: "100%",
        minWidth: 0,
        flexDirection: "column",
        gap: 0,
        listStyle: "none",
        padding: 0,
        margin: 0,
      }}
      {...props}
    >
      {children}
    </Stack>
  );
};

// Item del Menu del Sidebar
// forwardRef para poder envolverlo en un Tooltip (vista comprimida)
const SidebarMenuItem = React.forwardRef(({ children, ...props }, ref) => {
  return (
    <Box
      ref={ref}
      component="li"
      sx={{
        position: "relative",
      }}
      {...props}
    >
      {children}
    </Box>
  );
});

SidebarMenuItem.displayName = "SidebarMenuItem";

// Button del Menu del Sidebar
const SidebarMenuButton = React.forwardRef(
  ({ asChild = false, isActive = false, children, ...props }, ref) => {
    const { neutrals, colors } = useTheme();

    const buttonStyles = {
      display: "flex",
      width: "100%",
      alignItems: "center",
      gap: 2,
      overflow: "hidden",
      borderRadius: "10px",
      padding: 2,
      textAlign: "left",
      fontSize: "0.875rem",
      textDecoration: "none",
      color: isActive ? colors.brand.pink : "inherit",
      backgroundColor: isActive ? colors.secondary.main : "transparent",
      fontWeight: isActive ? 600 : 400,
      transition: "all 200ms ease-in-out",
      border: "none",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: isActive ? colors.secondary.main : neutrals.gray[50],
      },
      "&:focus": {
        outline: "none",
        backgroundColor: neutrals.gray[50],
      },
      "&:disabled": {
        pointerEvents: "none",
        opacity: 0.5,
      },
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, {
        ref,
        sx: buttonStyles,
        ...props,
      });
    }

    return (
      <Box ref={ref} component="button" sx={buttonStyles} {...props}>
        {children}
      </Box>
    );
  }
);

SidebarMenuButton.displayName = "SidebarMenuButton";

// Inset del Sidebar (área principal)
const SidebarInset = ({ docked = false, children, sx = {}, ...props }) => {
  const { theme } = useTheme();
  const { state, isMobile } = useSidebar();

  const dockedSx = docked
    ? {
        paddingTop: `${DOCKED_HEADER_HEIGHT}px`,
        paddingLeft: isMobile
          ? 0
          : `${
              state === "collapsed"
                ? DOCKED_SIDEBAR_WIDTH_ICON
                : DOCKED_SIDEBAR_WIDTH
            }px`,
        transition: `padding-left ${DOCKED_TRANSITION}`,
        boxSizing: "border-box",
      }
    : {};

  return (
    <Box
      component="main"
      sx={{
        backgroundColor: theme.palette.background.default,
        position: "relative",
        display: "flex",
        width: "100%",
        flex: 1,
        flexDirection: "column",
        ...dockedSx,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

// PropTypes
SidebarProvider.propTypes = {
  defaultOpen: PropTypes.bool,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  children: PropTypes.node,
};

Sidebar.propTypes = {
  side: PropTypes.oneOf(["left", "right"]),
  variant: PropTypes.oneOf(["sidebar", "floating", "inset", "docked"]),
  collapsible: PropTypes.oneOf(["offcanvas", "icon", "none"]),
  children: PropTypes.node,
};

SidebarHeader.propTypes = {
  children: PropTypes.node,
};

SidebarContent.propTypes = {
  children: PropTypes.node,
};

SidebarFooter.propTypes = {
  children: PropTypes.node,
};

SidebarMenu.propTypes = {
  children: PropTypes.node,
};

SidebarMenuItem.propTypes = {
  children: PropTypes.node,
};

SidebarMenuButton.propTypes = {
  asChild: PropTypes.bool,
  isActive: PropTypes.bool,
  children: PropTypes.node,
};

SidebarTrigger.propTypes = {
  sx: PropTypes.object,
};

SidebarInset.propTypes = {
  docked: PropTypes.bool,
  children: PropTypes.node,
  sx: PropTypes.object,
};


export {
  DOCKED_HEADER_HEIGHT,
  DOCKED_SIDEBAR_WIDTH,
  DOCKED_SIDEBAR_WIDTH_ICON,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
};
